/**
 * What one drop asks a view's `rank` endpoint to write, built from the cards on screen.
 *
 * The server sorts ranked cards ahead of every card nobody moved, and loaded cards are always
 * the first ones in that order, so every card above a drop is loaded. That is what lets the
 * client name the request: `after` is the last ranked card above the drop, and `rankFirst` is
 * the unranked cards above it, which are ranked ahead of the dropped card so it lands where the
 * reader put it.
 *
 * Layout-neutral: a board cell and a list are both cards in display order.
 */

import type {Anchor} from '@github-ui/optimistic-connection'

/** The body of one rank request, apart from the card it moves. */
export type ViewRankTarget = {
  /** The last ranked card above the drop, or `null` when the drop is above every ranked card. */
  after: string | null
  /** The unranked cards above the drop, top to bottom. */
  rankFirst: readonly string[]
}

export type ViewRankCard = {id: string; ranked: boolean}

/**
 * Returns the rank request for one drop.
 *
 * Returns `'none'` when the drop names no place: a cell that loaded no cards and has more, or the
 * end of a cell with more to load. The card can still move to the cell, but it has no rank to
 * write.
 *
 * Returns `'stale'` when the cards on screen cannot say where the drop is: the anchor card is
 * gone, or a ranked card is above the drop and some ranked card is drawn at a guessed position or
 * below an unranked one, which the server never sorts. Reload the cards instead of sending a
 * request.
 */
export function toViewRankTarget({
  cards,
  itemId,
  anchor,
  hasMoreCards,
  guessed,
}: {
  /** The cards where the drop lands, in display order. May include the dragged card. */
  cards: readonly ViewRankCard[]
  /** The dragged card. */
  itemId: string
  /** Where the card lands among `cards`, or `undefined` when the drop names no card. */
  anchor: Anchor | undefined
  /** Whether more cards follow the loaded ones. */
  hasMoreCards: boolean
  /**
   * Ranked cards drawn where the client guessed rather than where the server sorted them, such
   * as a ranked card moved to another cell whose rank failed to write.
   */
  guessed?: ReadonlySet<string>
}): ViewRankTarget | 'none' | 'stale' {
  if (anchor === undefined) return 'none'

  // Resolved against every card, because a multi-valued field can already show the dragged card
  // in the cell it is dropped in, and the drop can name that copy as its neighbor.
  const index = insertionIndex(cards, anchor, hasMoreCards)
  if (typeof index !== 'number') return index

  const above = cards.slice(0, index).filter(card => card.id !== itemId)
  const lastRanked = above.findLastIndex(card => card.ranked)
  // The write puts the card right after `after`, so a ranked card drawn out of place on either
  // side of the drop can really sit above `after`. A drop with nothing ranked above it goes first.
  if (lastRanked !== -1 && hasMisplacedRank(cards, itemId, guessed)) return 'stale'

  return {
    after: above[lastRanked]?.id ?? null,
    rankFirst: above.slice(lastRanked + 1).map(card => card.id),
  }
}

function hasMisplacedRank(cards: readonly ViewRankCard[], itemId: string, guessed?: ReadonlySet<string>): boolean {
  let isPastRanked = false
  for (const card of cards) {
    if (card.id === itemId) continue
    if (!card.ranked) isPastRanked = true
    else if (isPastRanked || guessed?.has(card.id)) return true
  }
  return false
}

function insertionIndex(
  cards: readonly ViewRankCard[],
  anchor: Anchor,
  hasMoreCards: boolean,
): number | 'none' | 'stale' {
  if ('at' in anchor) {
    if (anchor.at === 'start') return 0
    return hasMoreCards ? 'none' : cards.length
  }

  const neighbor = 'before' in anchor ? anchor.before : anchor.after
  const index = cards.findIndex(card => card.id === neighbor)
  if (index === -1) return 'stale'

  return 'before' in anchor ? index : index + 1
}
